import Patch from 'app/utils/Patch';
import {UNPROCESSABLE_ENTITY} from 'app/utils/HttpStatuses';
import {extractUnprocessableErrorMessage} from 'app/utils/ajaxError';
import I18n from 'app/i18n';
import type ErrorsServerInterface from 'app/interfaces/ErrorsServerInterface';
import {
    isIncubatorInitialPresetFieldErrors,
    type IncubatorInitialPresetsShape
} from 'app/utils/incubatorInitialPresetsFields';

export type IncubatorInitialPresetsPayload = IncubatorInitialPresetsShape;

interface UpdateIncubatorInitialPresetsResponse {
    incubator_initial_presets: IncubatorInitialPresetsPayload
}

export class IncubatorInitialPresetsValidationError extends Error {
    fieldErrors: ErrorsServerInterface;

    constructor(fieldErrors: ErrorsServerInterface) {
        super('Incubator initial presets validation failed');
        this.fieldErrors = fieldErrors;
    }
}

const isUpdateIncubatorInitialPresetsResponse = (
    json: unknown
): json is UpdateIncubatorInitialPresetsResponse => {
    if (!json || typeof json !== 'object') {
        return false;
    }

    const presets = (json as UpdateIncubatorInitialPresetsResponse).incubator_initial_presets;

    return typeof presets === 'object' && presets !== null;
};

const responseErrorMessage = (json: unknown): string => extractUnprocessableErrorMessage(json) ?? I18n.t('server_error_try_later');

export const updateIncubatorInitialPresets = async(
    attributes: IncubatorInitialPresetsPayload
): Promise<IncubatorInitialPresetsPayload> => {
    const response = await new Patch({
        params: {incubator_initial_presets: attributes},
        url: '/incubator/initial_presets'
    }).execute();
    const json = await response.json() as UpdateIncubatorInitialPresetsResponse | ErrorsServerInterface;

    if (response.status === UNPROCESSABLE_ENTITY && isIncubatorInitialPresetFieldErrors(json)) {
        throw new IncubatorInitialPresetsValidationError(json);
    }

    if (!response.ok) {
        throw new Error(responseErrorMessage(json));
    }

    if (!isUpdateIncubatorInitialPresetsResponse(json)) {
        throw new Error(I18n.t('server_error_try_later'));
    }

    return json.incubator_initial_presets;
};
